import { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Stack, Button, Alert, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress,
} from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { NamedMasterDto, NamedMasterListParams, NamedMasterPayload, PaginatedList } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { ITEM_TYPES, itemTypeLabel } from "@/shared/itemTypes";

interface NamedMasterApi {
  list: (params: NamedMasterListParams) => Promise<PaginatedList<NamedMasterDto>>;
  create: (payload: NamedMasterPayload) => Promise<{ id: string }>;
  update: (id: string, payload: NamedMasterPayload) => Promise<unknown>;
  delete: (id: string) => Promise<unknown>;
}

interface Props {
  /** Plural page title, e.g. "Item Categories". */
  title: string;
  /** Singular noun used in buttons/dialogs, e.g. "Category". */
  noun: string;
  api: NamedMasterApi;
  /** react-query cache key, e.g. "item-categories". */
  queryKey: string;
  /** Permission code prefix, e.g. "catalog.category". */
  permPrefix: string;
}

export function ReferenceNameMasterPage({ title, noun, api, queryKey, permPrefix }: Props) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<NamedMasterDto | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<NamedMasterDto | null>(null);

  const canCreate = useAuth(s => s.hasPermission(`${permPrefix}.create`));
  const canUpdate = useAuth(s => s.hasPermission(`${permPrefix}.edit`));
  const canDelete = useAuth(s => s.hasPermission(`${permPrefix}.delete`));

  const query = useQuery({
    queryKey: [queryKey, search, pagination.page, pagination.pageSize],
    queryFn: () => api.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: `${noun} deleted` },
    mutationFn: (id: string) => api.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [queryKey] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (row: NamedMasterDto) => { setEditing(row); setFormOpen(true); setMenuAnchor(null); };
  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };
  const handleDelete = (row: NamedMasterDto) => {
    closeMenu();
    if (confirm(`Delete ${noun.toLowerCase()} "${row.name}"? Blocked if any item references it.`)) {
      deleteMutation.mutate(row.id);
    }
  };

  const columns: GridColDef<NamedMasterDto>[] = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
    {
      field: "itemType", headerName: "Applies to", width: 160,
      valueGetter: (_v, row) => row.itemType ? itemTypeLabel(row.itemType) : "All types",
    },
    {
      field: "actions", headerName: "", width: 60, sortable: false, filterable: false,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuRow(p.row); }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>{title}</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New {noun.toLowerCase()}
          </Button>
        )}
      </Stack>
      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by name"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canUpdate && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {canUpdate && (
          <MenuItem onClick={() => menuRow && openEdit(menuRow)}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={() => menuRow && handleDelete(menuRow)}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <NameDialog
        open={formOpen} onClose={() => setFormOpen(false)}
        row={editing} noun={noun} api={api} queryKey={queryKey}
      />
    </Box>
  );
}

function NameDialog({
  open, onClose, row, noun, api, queryKey,
}: {
  open: boolean; onClose: () => void; row: NamedMasterDto | null;
  noun: string; api: NamedMasterApi; queryKey: string;
}) {
  const qc = useQueryClient();
  const isEdit = !!row;
  const [name, setName] = useState("");
  const [itemType, setItemType] = useState("");  // "" = global (all types)

  useEffect(() => {
    if (open) { setName(row?.name ?? ""); setItemType(row?.itemType ?? ""); }
  }, [open, row]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? `${noun} updated` : `${noun} created` },
    mutationFn: async () => {
      const payload = { name: name.trim(), itemType: itemType || null };
      if (isEdit && row) { await api.update(row.id, payload); return row.id; }
      const r = await api.create(payload);
      return r.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKey] });
      onClose();
    },
  });

  const err = !name.trim() ? "Name required" : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <DialogTitle>{isEdit ? `Edit ${noun.toLowerCase()}` : `New ${noun.toLowerCase()}`}</DialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          <Stack spacing={2}>
            <TextField label="Name *" size="small" fullWidth autoFocus
              value={name} onChange={(e) => setName(e.target.value)} />
            <TextField select label="Applies to item type" size="small" fullWidth
              value={itemType} onChange={(e) => setItemType(e.target.value)}
              helperText="Leave as “All types” to offer this everywhere">
              <MenuItem value=""><em>All types (global)</em></MenuItem>
              {ITEM_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            {err && <Alert severity="info">{err}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
